import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SidePanelAdmin from '@CommonFunctionalDisplay/SidePanelAdmin';
import TopPanelAdmin from '@CommonFunctionalDisplay/TopPanelAdmin';
import Modal from '@mui/material/Modal';
import { CheckCircle2, ArrowRight, Loader, X } from 'lucide-react';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import BillingService from '@Helpers/URLHelpers/BillingService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { detectBrowserPricingCurrency, formatBillingPrice, fetchExchangeRate } from '../../billingPricing';
import './SubscribePage.scss';

const billingService = new BillingService();

const FEATURE_LABELS = {
    multi_page_forms:    'Multi-page Forms',
    assignee_matrix:     'Assignee Matrix',
    priority_matrix:     'Priority Matrix',
    escalation_matrix:   'Escalation Matrix',
    label_matrix:        'Label Matrix',
    sla_tracking:        'SLA Tracking',
    advanced_reports:    'Advanced Reports',
    ai_summary:          'AI Summary',
    csat:                'CSAT',
    white_label:         'White Label',
    ai_chatbot:          'AI Chatbot',
};

const sortPlansById = (items = []) => [...items].sort((firstPlan, secondPlan) => {
    const firstId = firstPlan?.id;
    const secondId = secondPlan?.id;

    if (typeof firstId === 'number' && typeof secondId === 'number') {
        return firstId - secondId;
    }

    return String(firstId ?? '').localeCompare(String(secondId ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
});

export default function SubscribePage() {

    const navigate = useNavigate();
    const { currentOrg } = useReduxOrg();
    const { activeUser } = useReduxActiveUser();
    const [plans, setPlans] = useState([]);
    const [featureKeys, setFeatureKeys] = useState([]);
    const [billingCycle, setBillingCycle] = useState('MONTHLY');
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [couponCode, setCouponCode] = useState('');
    const [couponResult, setCouponResult] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [, setExchangeRateReady] = useState(false);
    const billingCurrency = detectBrowserPricingCurrency();
    const [contactForm, setContactForm] = useState({
        fullName: activeUser?.fullName || '',
        workEmail: activeUser?.email || '',
        organisation: currentOrg?.name || '',
        phoneNumber: '',
        teamSize: '',
        requirements: '',
    });

    const className = 'subscribePage';

    useEffect(() => {
        loadPlans();
        fetchExchangeRate().then(() => setExchangeRateReady(true));
    }, []);

    const loadPlans = async () => {
        setLoadingPlans(true);
        try {
            const data = await billingService.comparePlans();
            if (data?.body) {
                const parsed = JSON.parse(data.body);
                setPlans(sortPlansById(parsed.data?.plans || []));
                setFeatureKeys(parsed.data?.featureKeys || []);
            }
        } catch (err) {
            SnackbarManager.show('Failed to load plans', 'error');
        }
        setLoadingPlans(false);
    };

    const getDefaultContactForm = () => ({
        fullName: activeUser?.fullName || '',
        workEmail: activeUser?.email || '',
        organisation: currentOrg?.name || '',
        phoneNumber: '',
        teamSize: '',
        requirements: '',
    });

    const handleValidateCoupon = async () => {
        if (!couponCode.trim()) return;
        setValidatingCoupon(true);
        try {
            const data = await billingService.validateCoupon(couponCode.trim());
            if (data?.body) {
                const result = JSON.parse(data.body);
                setCouponResult(result);
                if (result.valid) {
                    SnackbarManager.show(`Coupon applied: ${result.discountPercent}% off!`, 'success');
                } else {
                    SnackbarManager.show(result.invalidReason || 'Invalid coupon', 'error');
                }
            }
        } catch (err) {
            SnackbarManager.show('Failed to validate coupon', 'error');
        }
        setValidatingCoupon(false);
    };

    const handleSubscribe = (plan) => {
        if (!currentOrg?.id || !activeUser?.email) {
            console.log(currentOrg, activeUser);
            SnackbarManager.show('Missing organisation or user info', 'error');
            return;
        }
        navigate('/checkout', {
            state: { plan, billingCycle, couponCode, couponResult },
        });
    };

    const isEnterprisePlan = (plan) => plan?.name?.toLowerCase().includes('enterprise');

    const openContactModal = () => {
        setContactForm(getDefaultContactForm());
        setIsContactModalOpen(true);
    };

    const closeContactModal = () => setIsContactModalOpen(false);

    const handleContactFieldChange = (event) => {
        const { name, value } = event.target;
        setContactForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleContactSubmit = (event) => {
        event.preventDefault();

        if (!contactForm.fullName.trim() || !contactForm.workEmail.trim() || !contactForm.organisation.trim()) {
            SnackbarManager.show('Please fill in name, work email, and organisation.', 'error');
            return;
        }

        SnackbarManager.show('Your enterprise enquiry has been captured. Connect the backend when ready.', 'success');
        setIsContactModalOpen(false);
    };

    const getButtonLabel = (plan) => isEnterprisePlan(plan) ? 'Contact Us' : 'Get Started';

    const getPrice = (plan) => {
        if (isEnterprisePlan(plan)) return 'Contact Us';
        const paise = billingCycle === 'MONTHLY' ? plan.priceMonthlyPaise : plan.priceAnnualPaise;
        return formatBillingPrice(paise, billingCurrency);
    };

    const getPriceSuffix = () => billingCycle === 'MONTHLY' ? '/mo' : '/yr';

    if (loadingPlans) {
        return (
            <div className={className + '__loading'}>
                <Loader size={32} className={className + '__loading__spinner'} />
                <p>Loading plans...</p>
            </div>
        );
    }

    return (
        <div className={className}>
            <SidePanelAdmin />
            <TopPanelAdmin />
            <div className={className + '__hero'}>
                <h2>Choose a plan to get started</h2>
                <p>Select the plan that fits your team. You can upgrade or change at any time.</p>
            </div>

            <div className={className + '__content'}>

                {/* Billing cycle toggle */}
                <div className={className + '__cycle-toggle'}>
                    <button
                        className={className + '__cycle-btn' + (billingCycle === 'MONTHLY' ? ' ' + className + '__cycle-btn--active' : '')}
                        onClick={() => setBillingCycle('MONTHLY')}
                    >
                        Monthly
                    </button>
                    <button
                        className={className + '__cycle-btn' + (billingCycle === 'ANNUAL' ? ' ' + className + '__cycle-btn--active' : '')}
                        onClick={() => setBillingCycle('ANNUAL')}
                    >
                        Annual <span className={className + '__cycle-badge'}>Save ~20%</span>
                    </button>
                </div>

                {/* Coupon input */}
                <div className={className + '__coupon'}>
                    <input
                        className={className + '__coupon__input'}
                        type="text"
                        placeholder="Have a coupon code?"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                    />
                    <button
                        className={className + '__coupon__btn'}
                        onClick={handleValidateCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                    >
                        {validatingCoupon ? <Loader size={14} /> : 'Apply'}
                    </button>
                    {couponResult?.valid && (
                        <span className={className + '__coupon__success'}>
                            <CheckCircle2 size={14} /> {couponResult.discountPercent}% off applied
                        </span>
                    )}
                </div>

                {/* Plan cards */}
                <div className={className + '__plans'}>
                    {plans.map((plan) => (
                        <div key={plan.id} className={className + '__plan-card'}>
                            <div className={className + '__plan-card__header'}>
                                <h3 className={className + '__plan-card__name'}>{plan.name}</h3>
                                <div className={className + '__plan-card__price'}>
                                    <span className={className + '__plan-card__price__amount'}>{getPrice(plan)}</span>
                                    {!isEnterprisePlan(plan) && getPrice(plan) !== 'Free' && (
                                        <span className={className + '__plan-card__price__suffix'}>{getPriceSuffix()}</span>
                                    )}
                                </div>
                                <div className={className + '__plan-card__limits'}>
                                    <span>{plan.maxAgents === -1 ? 'Unlimited agents' : `Up to ${plan.maxAgents} agents`}</span>
                                    <span>{plan.ticketLimitMonthly === -1 ? 'Unlimited tickets/mo' : `${plan.ticketLimitMonthly} tickets/mo`}</span>
                                </div>
                            </div>

                            <ul className={className + '__plan-card__features'}>
                                {[...featureKeys]
                                    .sort((a, b) => Number(!!plan.features?.[b]) - Number(!!plan.features?.[a]))
                                    .map((key) => (
                                    <li
                                        key={key}
                                        className={className + '__plan-card__feature' + (!plan.features?.[key] ? ' ' + className + '__plan-card__feature--disabled' : '')}
                                    >
                                        <CheckCircle2 size={14} />
                                        <span>{FEATURE_LABELS[key] || key}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                className={className + '__plan-card__btn'}
                                onClick={() => isEnterprisePlan(plan) ? openContactModal() : handleSubscribe(plan)}
                            >
                                {getButtonLabel(plan)} <ArrowRight size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <Modal
                open={isContactModalOpen}
                onClose={closeContactModal}
                aria-labelledby="enterprise-contact-title"
                aria-describedby="enterprise-contact-description"
            >
                <div className={className + '__contact-modal'}>
                    <button
                        type="button"
                        className={className + '__contact-modal__close'}
                        onClick={closeContactModal}
                        aria-label="Close contact form"
                    >
                        <X size={18} />
                    </button>

                    <div className={className + '__contact-modal__header'}>
                        <span className={className + '__contact-modal__eyebrow'}>Enterprise Plan</span>
                        <h2 id="enterprise-contact-title">Talk to us about enterprise rollout</h2>
                        <p id="enterprise-contact-description">
                            Share your team details and requirements. The UI is ready now; you can connect the submission to your backend later.
                        </p>
                    </div>

                    <form className={className + '__contact-modal__form'} onSubmit={handleContactSubmit}>
                        <div className={className + '__contact-modal__grid'}>
                            <label className={className + '__contact-modal__field'}>
                                <span>Full name</span>
                                <input
                                    name="fullName"
                                    type="text"
                                    value={contactForm.fullName}
                                    onChange={handleContactFieldChange}
                                    placeholder="Jane Smith"
                                />
                            </label>

                            <label className={className + '__contact-modal__field'}>
                                <span>Work email</span>
                                <input
                                    name="workEmail"
                                    type="email"
                                    value={contactForm.workEmail}
                                    onChange={handleContactFieldChange}
                                    placeholder="jane@company.com"
                                />
                            </label>

                            <label className={className + '__contact-modal__field'}>
                                <span>Organisation</span>
                                <input
                                    name="organisation"
                                    type="text"
                                    value={contactForm.organisation}
                                    onChange={handleContactFieldChange}
                                    placeholder="Acme Ltd"
                                />
                            </label>

                            <label className={className + '__contact-modal__field'}>
                                <span>Phone number</span>
                                <input
                                    name="phoneNumber"
                                    type="tel"
                                    value={contactForm.phoneNumber}
                                    onChange={handleContactFieldChange}
                                    placeholder="+91 98765 43210"
                                />
                            </label>

                            <label className={className + '__contact-modal__field'}>
                                <span>Team size</span>
                                <input
                                    name="teamSize"
                                    type="text"
                                    value={contactForm.teamSize}
                                    onChange={handleContactFieldChange}
                                    placeholder="250 employees"
                                />
                            </label>
                        </div>

                        <label className={className + '__contact-modal__field'}>
                            <span>Requirements</span>
                            <textarea
                                name="requirements"
                                rows="5"
                                value={contactForm.requirements}
                                onChange={handleContactFieldChange}
                                placeholder="Tell us about use cases, rollout size, integrations, SLAs, or support expectations."
                            />
                        </label>

                        <div className={className + '__contact-modal__actions'}>
                            <button type="button" className={className + '__contact-modal__secondary'} onClick={closeContactModal}>
                                Cancel
                            </button>
                            <button type="submit" className={className + '__contact-modal__primary'}>
                                Submit details
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>

        </div>
    );
}
